import React from "react";
import {
  TrendUp, CheckCircle, Warning, Lightning, Bell, ChartLine, Brain, Target, CircleNotch,
} from "@phosphor-icons/react";
import AutoDebitConsent from "./AutoDebitConsent";

const FEATURES = [
  { icon: TrendUp, text: "Daily high-probability breakout signals (6M + 3Y)" },
  { icon: Target, text: "Pre-calculated Entry, Stop-Loss & Target (1:2 R:R)" },
  { icon: Lightning, text: "Volume × trend confirmation (≥ 2× avg)" },
  { icon: Brain, text: "AI fundamental verdict (Strong / Neutral / Weak) via Gemini" },
  { icon: ChartLine, text: "Sector strength + Top 5 movers by volume surge at 9:25 IST" },
  { icon: Bell, text: "Instant alerts when SL / Target / Exit rule triggers" },
];

export default function PricingCard({
  plan, isActive, status, agreed, setAgreed, busy, err,
  onStartTrial, onCancel,
}) {
  const trialPaise = plan ? (plan.trial_paise / 100).toFixed(0) : "5";
  const monthlyPaise = plan ? (plan.monthly_paise / 100).toFixed(0) : "299";
  const trialDays = plan?.trial_days ?? 5;

  return (
    <div className="border border-subtle rounded-sm overflow-hidden bg-surface">
      {/* Headline price */}
      <div className="p-8 border-b border-subtle bg-pos-soft">
        <div className="flex items-baseline gap-4 flex-wrap">
          <div className="text-[10px] font-mono uppercase tracking-widest text-pos">Limited time offer</div>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            auto-renew · cancel anytime
          </span>
        </div>
        <div className="mt-3 flex items-baseline gap-4 flex-wrap">
          <div className="font-mono text-5xl text-primary-fg">₹{trialPaise}</div>
          <div className="text-secondary-fg">for {trialDays} days</div>
          <div className="mx-3 text-muted-fg">→</div>
          <div className="font-mono text-2xl text-primary-fg">
            ₹{monthlyPaise}<span className="text-secondary-fg text-base"> /month</span>
          </div>
        </div>
        <p className="text-sm text-secondary-fg mt-3 max-w-2xl">
          Pay just ₹{trialPaise} today to authorise auto-payment. After the {trialDays}-day trial, ₹{monthlyPaise} will
          be charged to your UPI / card every month automatically. Cancel anytime from the dashboard.
        </p>
      </div>

      {/* Features */}
      <div className="p-8">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-4">/// What you get</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURES.map((f) => (
            <div key={f.text} className="flex items-start gap-3">
              <div className="shrink-0 p-2 border border-subtle rounded-sm bg-app">
                <f.icon size={16} className="text-pos" weight="bold" />
              </div>
              <div className="text-sm text-primary-fg/90 leading-snug pt-1">{f.text}</div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          {isActive ? (
            <div className="space-y-3" data-testid="active-subscription">
              <div className="flex items-center gap-2 text-pos font-medium">
                <CheckCircle size={18} weight="fill" />
                Premium active · status <span className="font-mono uppercase">{status}</span>
              </div>
              <p className="text-sm text-secondary-fg">
                Your subscription is active. You can cancel anytime — access will continue until the end of your current
                billing cycle.
              </p>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                data-testid="cancel-subscription"
                className="px-4 py-2 border border-neg-soft rounded-sm text-neg hover:bg-neg-soft transition text-sm"
              >
                {busy ? "Cancelling…" : "Cancel subscription"}
              </button>
            </div>
          ) : (
            <>
              <AutoDebitConsent
                agreed={agreed}
                setAgreed={setAgreed}
                monthlyPrice={monthlyPaise}
                trialDays={trialDays}
              />
              <button
                type="button"
                onClick={onStartTrial}
                disabled={busy || !agreed}
                data-testid="start-trial"
                className="w-full md:w-auto px-8 py-4 rounded-sm font-medium text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: "hsl(var(--signal-positive))", color: "#fff" }}
              >
                {busy ? (
                  <>
                    <CircleNotch className="animate-spin" size={16} weight="bold" />
                    Opening payment…
                  </>
                ) : (
                  <>
                    <Lightning size={16} weight="fill" />
                    Start ₹{trialPaise} Trial · {trialDays} days
                  </>
                )}
              </button>
            </>
          )}

          {err && (
            <div className="mt-4 p-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm">
              {err}
            </div>
          )}
        </div>
      </div>

      {/* Important terms */}
      <div className="p-6 border-t border-subtle bg-app">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2 flex items-center gap-2">
          <Warning size={12} weight="bold" /> Important
        </div>
        <ul className="text-xs text-secondary-fg space-y-1 list-disc list-inside">
          <li>Subscription will <span className="text-primary-fg">auto-renew every month</span> after the {trialDays}-day trial.</li>
          <li>₹{monthlyPaise} will be charged automatically to your authorised payment method.</li>
          <li>No refund after renewal. You can cancel anytime before the next billing date.</li>
          <li>Payments are processed securely via Razorpay — UPI / Cards / Net Banking.</li>
          <li className="text-mod"><b>Breakout Scanner is NOT a SEBI-registered Research Analyst.</b> Signals are educational/technical observations, not investment advice. Consult your SEBI-registered financial advisor before any trade.</li>
        </ul>
      </div>
    </div>
  );
}
